<template>
  <div class="container-fluid">
    <div class="block-header">
      <bread-crumb :breadCrumbIteams="breadCrumbIteams"></bread-crumb>
    </div>
    <div class="row clearfix">
      <div class="col-lg-12">
        <!-- Summary Cards -->
        <div class="row mb-3">
          <div class="col-lg-2 col-md-4 col-sm-6 mb-2" v-for="(stat, idx) in summaryCards" :key="idx">
            <div
              class="card mb-0 cursor-pointer"
              :class="{ 'border-primary': activeStatusFilter === stat.key }"
              @click="toggleStatusFilter(stat.key)"
            >
              <div class="body text-center py-3">
                <h4 class="mb-0" :class="stat.textClass">{{ stat.count }}</h4>
                <small class="text-muted">{{ stat.label }}</small>
              </div>
            </div>
          </div>
        </div>

        <div class="card card-top-line">
          <div class="header d-flex justify-content-between align-items-center">
            <h2>{{ pageTitle }}</h2>
          </div>
          <div class="body pt-0">
            <!-- Filters -->
            <div class="chart-top-action d-lg-flex justify-content-between py-3 fresh-customer-filter">
              <div class="d-flex flex-wrap align-items-center">
                <b-form-select
                  v-model="filters.status"
                  :options="statusOptions"
                  size="sm"
                  class="mr-2 mb-2"
                  style="width: 180px;"
                  @change="applyFilters"
                ></b-form-select>
                <b-form-select
                  v-model="filters.assigned_to"
                  :options="assignedToOptions"
                  size="sm"
                  class="mr-2 mb-2"
                  style="width: 180px;"
                  @change="applyFilters"
                ></b-form-select>
                <b-form-select
                  v-model="filters.sla"
                  :options="slaOptions"
                  size="sm"
                  class="mr-2 mb-2"
                  style="width: 150px;"
                  @change="applyFilters"
                ></b-form-select>
              </div>
              <div class="d-flex align-items-center">
                <div class="input-group mb-2" style="width: 250px;">
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    placeholder="Search business name..."
                    v-model="filters.search"
                    v-on:keyup.enter="applyFilters"
                  />
                  <div class="input-group-append" @click="applyFilters">
                    <span class="input-group-text"><i class="icon-magnifier"></i></span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Loading Indicator -->
            <div v-if="isLoading" class="text-center py-4">
              <b-spinner variant="primary"></b-spinner>
              <div class="text-muted mt-2">Loading submissions...</div>
            </div>

            <!-- Error State -->
            <div v-else-if="storeError" class="alert alert-danger">
              <i class="fa fa-exclamation-circle mr-1"></i> {{ storeError }}
              <b-button variant="outline-danger" size="sm" class="ml-3" @click="loadSubmissions">Retry</b-button>
            </div>

            <template v-else>
              <!-- Table Pagination Top -->
              <div class="py-1 align-items-center justify-content-end table-pagination">
                <div class="number-page">
                  <b-pagination
                    class="pagination-small"
                    v-model="currentPage"
                    :total-rows="totalRows"
                    first-text="First"
                    last-text="Last"
                    :per-page="perPage"
                    align="right"
                    @change="onPageChange"
                  ></b-pagination>
                </div>
              </div>

              <!-- Submissions Table -->
              <b-table
                responsive
                table-class="mb-0 table-custom border"
                head-variant="light"
                :items="submissions"
                :fields="fields"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :no-local-sorting="true"
                @sort-changed="onSortChanged"
                striped
                show-empty
              >
                <template #head()="{ label, field: { key } }">
                  {{ label }}
                  <template>
                    <i v-if="sortBy !== key" class=""></i>
                    <i v-else-if="sortDesc" class="fa fa-long-arrow-down ml-1"></i>
                    <i v-else class="fa fa-long-arrow-up ml-1"></i>
                  </template>
                </template>

                <template #cell(business_name)="row">
                  <a class="font-weight-bold text-primary cursor-pointer" @click="viewSubmission(row.item)">
                    {{ row.value }}
                  </a>
                </template>

                <template #cell(status)="row">
                  <span class="badge" :class="statusBadgeClass(row.value)">
                    {{ statusLabel(row.value) }}
                  </span>
                </template>

                <template #cell(tier1_status)="row">
                  <span v-if="row.value" class="badge" :class="tierBadgeClass(row.value)">
                    {{ tierLabel(row.value) }}
                  </span>
                  <span v-else class="text-muted">—</span>
                </template>

                <template #cell(tier2_status)="row">
                  <span v-if="row.value" class="badge" :class="tierBadgeClass(row.value)">
                    {{ tierLabel(row.value) }}
                  </span>
                  <span v-else class="text-muted">—</span>
                </template>

                <template #cell(submitted_at)="row">
                  {{ formatDate(row.value) }}
                </template>

                <template #cell(sla_deadline)="row">
                  <span :class="slaClass(row.value, row.item.status)">
                    {{ formatDate(row.value) }}
                    <i v-if="isSlaOverdue(row.value, row.item.status)" class="fa fa-exclamation-circle ml-1"></i>
                  </span>
                </template>

                <template #cell(assigned_to)="row">
                  <span v-if="row.item.assignee_name">{{ row.item.assignee_name }}</span>
                  <span v-else-if="row.value" class="text-muted font-italic">Assigned</span>
                  <span v-else class="text-muted font-italic">Unassigned</span>
                </template>

                <template #cell(actions)="row">
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    @click="viewSubmission(row.item)"
                  >
                    <i class="fa fa-eye mr-1"></i> View
                  </b-button>
                </template>

                <template #empty>
                  <div class="text-center text-muted py-4">
                    No submissions match the current filters.
                  </div>
                </template>
              </b-table>

              <!-- Table Pagination Bottom -->
              <div class="d-flex py-3 align-items-center justify-content-between table-pagination">
                <div class="per-page">
                  <b-form-group label="Per page" label-for="per-page-select" class="mb-0">
                    <b-form-select
                      id="per-page-select"
                      v-model="perPage"
                      :options="pageOptions"
                      size="sm"
                      @change="onPerPageChange"
                    ></b-form-select>
                  </b-form-group>
                </div>
                <div class="number-page">
                  <b-pagination
                    v-model="currentPage"
                    :total-rows="totalRows"
                    first-text="First"
                    last-text="Last"
                    :per-page="perPage"
                    align="right"
                    @change="onPageChange"
                  ></b-pagination>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BreadCrumb from "@/components/BreadCrumb.vue";
import { mapGetters } from "vuex";

export default {
  name: "SubmissionsQueue",
  components: {
    "bread-crumb": BreadCrumb,
  },
  props: {
    assignedToMe: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      breadCrumbIteams: [
        { title: "Onboarding", href: "/onboarding/submissions" },
        { title: this.assignedToMe ? "Assigned to Me" : "Submissions Queue" },
      ],
      activeStatusFilter: null,
      filters: {
        status: null,
        assigned_to: null,
        sla: null,
        search: "",
      },
      fields: [
        { key: "business_name", label: "Business Name", sortable: true },
        { key: "country", label: "Country", sortable: true },
        { key: "business_type", label: "Business Type", sortable: true },
        { key: "submitted_at", label: "Submitted At", sortable: true },
        { key: "status", label: "Status", sortable: true },
        { key: "tier1_status", label: "Tier 1", sortable: true },
        { key: "tier2_status", label: "Tier 2", sortable: true },
        { key: "sla_deadline", label: "SLA Deadline", sortable: true },
        { key: "assigned_to", label: "Assigned To", sortable: true },
        { key: "actions", label: "Actions", sortable: false },
      ],
      sortBy: "submitted_at",
      sortDesc: true,
      currentPage: 1,
      perPage: 20,
      pageOptions: [10, 20, 50, 100],
    };
  },
  computed: {
    ...mapGetters("onboarding", ["submissions", "summary", "pagination", "isLoading", "error"]),
    storeError() {
      return this.$store.getters["onboarding/error"];
    },
    pageTitle() {
      return this.assignedToMe ? "Assigned to Me" : "Submissions Queue";
    },
    totalRows() {
      return this.pagination.total_items || 0;
    },
    currentUserId() {
      const user = this.$store.getters.currentuser;
      return user?.userid || user?.id || null;
    },
    statusOptions() {
      return [
        { value: null, text: "All Statuses" },
        { value: "pending_review", text: "Pending Review" },
        { value: "in_review", text: "In Review" },
        { value: "requires_attention", text: "Requires Attention" },
        { value: "tier1_approved", text: "Tier 1 Approved" },
        { value: "tier2_approved", text: "Fully Approved" },
        { value: "rejected", text: "Rejected" },
      ];
    },
    assignedToOptions() {
      return [
        { value: null, text: "All Reviewers" },
        { value: "unassigned", text: "Unassigned" },
      ];
    },
    slaOptions() {
      return [
        { value: null, text: "All SLA" },
        { value: "overdue", text: "Overdue" },
        { value: "due_today", text: "Due Today" },
        { value: "on_track", text: "On Track" },
      ];
    },
    summaryCards() {
      return [
        { key: "pending_review", label: "Pending Review", count: this.summary.pending_review || 0, textClass: "text-primary" },
        { key: "in_review", label: "In Review", count: this.summary.in_review || 0, textClass: "text-info" },
        { key: "requires_attention", label: "Requires Attention", count: this.summary.requires_attention || 0, textClass: "text-warning" },
        { key: "tier1_approved", label: "Tier 1 Approved", count: this.summary.tier1_approved || 0, textClass: "text-info" },
        { key: "tier2_approved", label: "Fully Approved", count: this.summary.tier2_approved || 0, textClass: "text-success" },
        { key: "rejected", label: "Rejected", count: this.summary.rejected || 0, textClass: "text-danger" },
      ];
    },
  },
  created() {
    this.loadSubmissions();
  },
  methods: {
    buildQueryParams() {
      const params = {
        page: this.currentPage,
        limit: this.perPage,
        sort: this.sortBy,
        order: this.sortDesc ? "desc" : "asc",
      };
      if (this.assignedToMe && this.currentUserId) {
        params.assigned_to = this.currentUserId;
      }
      const statusParts = [];
      if (this.activeStatusFilter) statusParts.push(this.activeStatusFilter);
      if (this.filters.status) statusParts.push(this.filters.status);
      if (statusParts.length) params.status = statusParts.join(",");
      if (!this.assignedToMe && this.filters.assigned_to) {
        params.assigned_to = this.filters.assigned_to;
      }
      if (this.filters.sla) params.sla = this.filters.sla;
      if (this.filters.search) params.search = this.filters.search;
      return params;
    },
    async loadSubmissions() {
      try {
        await this.$store.dispatch("onboarding/fetchSubmissions", this.buildQueryParams());
      } catch {
        // Error is already set in the store
      }
    },
    toggleStatusFilter(key) {
      this.activeStatusFilter = this.activeStatusFilter === key ? null : key;
      this.currentPage = 1;
      this.loadSubmissions();
    },
    applyFilters() {
      this.currentPage = 1;
      this.loadSubmissions();
    },
    onPageChange(page) {
      this.currentPage = page;
      this.loadSubmissions();
    },
    onPerPageChange() {
      this.currentPage = 1;
      this.loadSubmissions();
    },
    onSortChanged(ctx) {
      this.sortBy = ctx.sortBy;
      this.sortDesc = ctx.sortDesc;
      this.currentPage = 1;
      this.loadSubmissions();
    },
    viewSubmission(item) {
      this.$router.push(`/onboarding/submissions/${item.id}`);
    },
    formatDate(dateStr) {
      if (!dateStr) return "—";
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    },
    statusLabel(status) {
      const map = {
        draft: "Draft",
        pending_review: "Pending Review",
        in_review: "In Review",
        requires_attention: "Requires Attention",
        tier1_approved: "Tier 1 Approved",
        tier2_approved: "Fully Approved",
        rejected: "Rejected",
        withdrawn: "Withdrawn",
      };
      return map[status] || status;
    },
    statusBadgeClass(status) {
      const map = {
        draft: "badge-default",
        pending_review: "badge-primary",
        in_review: "badge-info",
        requires_attention: "badge-warning",
        tier1_approved: "badge-info",
        tier2_approved: "badge-success",
        rejected: "badge-danger",
        withdrawn: "badge-default",
      };
      return map[status] || "badge-secondary";
    },
    tierLabel(status) {
      const map = {
        pending: "Pending",
        approved: "Approved",
        rejected: "Rejected",
        changes_requested: "Changes Requested",
      };
      return map[status] || status;
    },
    tierBadgeClass(status) {
      const map = {
        pending: "badge-default",
        approved: "badge-success",
        rejected: "badge-danger",
        changes_requested: "badge-warning",
      };
      return map[status] || "badge-secondary";
    },
    slaClass(deadline, status) {
      const isTerminal = ["tier2_approved", "rejected", "withdrawn"].includes(status);
      if (isTerminal) return "text-muted";
      const now = new Date();
      const d = new Date(deadline);
      const hoursLeft = (d - now) / (1000 * 60 * 60);
      if (hoursLeft < 0) return "text-danger font-weight-bold";
      if (hoursLeft < 48) return "text-warning";
      return "text-success";
    },
    isSlaOverdue(deadline, status) {
      const isTerminal = ["tier2_approved", "rejected", "withdrawn"].includes(status);
      if (isTerminal) return false;
      return new Date(deadline) < new Date();
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.card.border-primary {
  border: 2px solid #007bff !important;
}
</style>
